/**
 * Small, framework-free rules behind the board's Measure & estimate toolbar
 * (web/miro-excalidraw/src/MeasureToolbar.tsx): which double-clicks edit a
 * line's points, removing the last vertex of a run being drawn, Shift-snapping
 * a calibration line, and the step texts the banners show.
 */
import type { MeasureDisplay, Pt } from './measure';

export interface DblClickSettings {
  /** Double-click a plain line → point editor (else Excalidraw's text label). */
  line: boolean;
  /** Double-click an arrow → point editor (else its text label, Excalidraw's default). */
  arrow: boolean;
}

/** Lines edit points on double-click, arrows keep their labels; measured runs always edit points. */
export const DEFAULT_DBLCLICK: DblClickSettings = { line: true, arrow: false };

export function dblClickEditsPoints(el: { type: string; elbowed?: boolean }, measured: boolean, s: DblClickSettings = DEFAULT_DBLCLICK): boolean {
  if (el.type !== 'line' && el.type !== 'arrow') return false;
  if (el.elbowed) return false; // Excalidraw has no point editor for elbow arrows
  if (measured) return true;
  return el.type === 'line' ? s.line : s.arrow;
}

/**
 * Backspace while drawing a multi-point line. Excalidraw adds a point that follows
 * the cursor once the mouse moves after a click (until then the last point is the
 * last committed one, `lastCommittedPoint`). Drops the last committed vertex and
 * keeps the floating point if there is one; null when only the start point would
 * be left (cancel the run instead).
 */
export function removeLastVertex<T extends readonly [number, number]>(
  points: readonly T[],
  lastCommitted: readonly [number, number] | null | undefined,
): { points: T[]; lastCommitted: T } | null {
  const n = points.length;
  if (!lastCommitted || n < 2) return null; // only the start point so far
  const last = points[n - 1];
  const floating = !(last[0] === lastCommitted[0] && last[1] === lastCommitted[1]);
  const committed = floating ? n - 1 : n;
  if (committed <= 2) return null;
  const kept = floating ? [...points.slice(0, n - 2), last] : points.slice(0, n - 1);
  return { points: kept, lastCommitted: floating ? kept[kept.length - 2] : kept[kept.length - 1] };
}

/** Shift held on the second calibration click: keep the line horizontal or vertical. */
export function snapAxis(a: Pt, b: Pt): Pt {
  return Math.abs(b[0] - a[0]) >= Math.abs(b[1] - a[1]) ? [b[0], a[1]] : [a[0], b[1]];
}

/** Distance from p to the nearest segment of a polyline. */
export function distanceToPolyline(pts: readonly Pt[], p: Pt): number {
  if (!pts.length) return Infinity;
  if (pts.length === 1) return Math.hypot(p[0] - pts[0][0], p[1] - pts[0][1]);
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1];
    const [bx, by] = pts[i];
    const dx = bx - ax;
    const dy = by - ay;
    const L = dx * dx + dy * dy;
    const t = L ? Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / L)) : 0;
    best = Math.min(best, Math.hypot(p[0] - (ax + t * dx), p[1] - (ay + t * dy)));
  }
  return best;
}

const DISPLAY_ORDER: MeasureDisplay[] = ['ftin', 'ft', 'm'];
export const DISPLAY_SHORT: Record<MeasureDisplay, string> = { ftin: `ft-in`, ft: 'ft', m: 'm' };

/** Units button: feet-inches → decimal feet → metres → … */
export function nextDisplay(d: MeasureDisplay | null | undefined): MeasureDisplay {
  const i = DISPLAY_ORDER.indexOf(d ?? 'ftin');
  return DISPLAY_ORDER[(i + 1) % DISPLAY_ORDER.length];
}

/** Guided calibration: the banner for where the user is. */
export function calibrationStep(hasImage: boolean, points: number): { step: 0 | 1 | 2 | 3; text: string } {
  if (!hasImage) return { step: 0, text: 'Click the drawing you want to scale' };
  if (points === 0) return { step: 1, text: 'Step 1 of 3 · Click the start of a known dimension (e.g. the 0 mark of the scale bar)' };
  if (points === 1) return { step: 2, text: 'Step 2 of 3 · Click the end of it (e.g. the 10 m mark) · hold Shift to keep it straight' };
  return { step: 3, text: 'Step 3 of 3 · Type the real length between the two points' };
}

export function measuringBanner(kind: 'linear' | 'area', what: string | null): string {
  const head = kind === 'area' ? `Measuring an area${what ? ` of ${what}` : ''}` : `Measuring a run${what ? ` of ${what}` : ''}`;
  return `${head} · click points ${kind === 'area' ? 'around it' : 'along it'} · double-click or Enter to finish · Esc to cancel · Backspace removes the last point`;
}

/** Step banner while placing a dimension. */
export function dimensionBanner(clicks: number): string {
  return clicks === 0
    ? 'Dimension · click the start · Shift keeps it straight · Esc cancels'
    : 'Dimension · click the end · Shift keeps it straight · Esc cancels';
}

/** The unit the last calibration used, so the next one is pre-filled ("10 m" → " m"). */
export function lengthUnitSuffix(lastUnit: 'ft' | 'm' | null | undefined): string {
  // Feet are typed many ways (24', 24'-6", 288"), so only metres get a suffix.
  return lastUnit === 'm' ? ' m' : '';
}
